import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

export interface RememberDevicePromptProps {
  open: boolean;
  /** Continue past this prompt without (or after) remembering the device. */
  onContinue: () => void;
  /** Persist device trust. `onContinue` is called automatically once this resolves. */
  onRemember: () => Promise<void> | void;
  loading?: boolean;
}

/**
 * Post-MFA prompt offering to trust this browser for 30 days so future logins
 * skip the MFA challenge. Backend-agnostic — the host app supplies `onRemember`.
 */
export const RememberDevicePrompt: React.FC<RememberDevicePromptProps> = ({
  open,
  onContinue,
  onRemember,
  loading = false,
}) => {
  const [remembering, setRemembering] = useState(false);
  const busy = loading || remembering;

  const handleRemember = async () => {
    setRemembering(true);
    try {
      await onRemember();
    } finally {
      setRemembering(false);
    }
    onContinue();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onContinue} maxWidth="xs" fullWidth>
      <DialogTitle>Remember this device?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Stay signed in on this browser for 30 days without MFA each time.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 3, pb: 3, gap: 1 }}>
        <Button variant="contained" onClick={() => void handleRemember()} disabled={busy} fullWidth>
          {remembering ? <CircularProgress size={22} /> : 'Remember this device'}
        </Button>
        <Button variant="text" onClick={onContinue} disabled={busy} fullWidth>
          Continue without remembering
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RememberDevicePrompt;
